import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';

import { DynamicFieldType } from 'components/DynamicFormField/DynamicFormField';
import { useWidgetContext } from '../Widget';
import { useDashboardContext } from 'screens/dashboard/Dashboard';
import type { DashboardsProviderType } from 'providers/contexts/extensions/Dashboards';
import { STALE_TIME } from '../shared/constants';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { getDashboardLink } from 'screens/dashboard/custom-dashboards/utils/functions';

export default function useWidgetAlerts({ isEnabled }: { isEnabled: boolean }) {
  const { widget } = useWidgetContext();
  const { dashboard } = useDashboardContext();
  const dataProvider = useDataProvider();
  const { activeSite } = useCosmos();

  // Fetch alert fields configuration for this widget.
  const { data: alertFieldsData, isLoading: isLoadingAlertFields } = useQuery({
    queryKey: ['widget-alert-fields', widget?.id],
    queryFn: async () => {
      if (!widget?.id) return [];

      try {
        const extendedDataProvider = dataProvider as typeof dataProvider &
          DashboardsProviderType;

        const response = await extendedDataProvider.getWidgetAlertFields(widget.id);
        return (response?.data || []) as DynamicFieldType[];
      } catch (error) {
        console.error('[useWidgetAlerts] Error fetching alert fields:', error);
        return [];
      }
    },
    enabled: Boolean(widget?.id) && isEnabled,
    staleTime: STALE_TIME, // Cache for 60 seconds
    retry: false, // A 400 means this widget doesn't support alerts; don't retry.
  });

  // Use the fetched alert fields, wrapped in useMemo to prevent recreating dependencies
  const alertFields = useMemo(() => alertFieldsData || [], [alertFieldsData]);

  // Fetch existing alert for this widget
  const { data: existingAlert, isLoading: isLoadingAlert } = useQuery({
    queryKey: ['widget-alert', widget?.id, dashboard?.id],
    queryFn: async () => {
      if (!widget?.id || !dashboard?.id) return null;

      try {
        const extendedDataProvider = dataProvider as typeof dataProvider &
          DashboardsProviderType;

        // Fetch alerts for this widget.
        const response = await extendedDataProvider.getWidgetAlerts([widget.id]);

        // Return the first (and should be only) alert for this widget
        return response?.items?.[0] || null;
      } catch (error) {
        console.error('[useWidgetAlerts] Error fetching alert:', error);
        return null;
      }
    },
    enabled: Boolean(widget?.id && dashboard?.id) && isEnabled,
    staleTime: STALE_TIME,
  });

  // Compute default values for a NEW alert from field definitions.
  // Existing alert values are handled separately via reset() in WidgetAlerts when editing.
  const defaultValues = useMemo(() => {
    // This creates a link that navigates to the dashboard and scrolls to the specific widget
    // Format: https://example.com/site/dashboard/path#widget-{widgetId}
    let generatedRedirectUrl = '';
    if (dashboard && widget?.id && activeSite) {
      try {
        // Use getDashboardLink to get the proper dashboard URL
        const dashboardPath = getDashboardLink({
          baseUrl: `/${activeSite}`,
          dashboardData: dashboard as any,
        });

        // Only generate if we got a valid path (not '#')
        if (dashboardPath && dashboardPath !== '#') {
          // Add the full origin and append widget anchor for navigation
          // The anchor (#widget-{id}) matches the WidgetCard's id attribute for auto-scrolling
          generatedRedirectUrl = `${window.location.origin}${dashboardPath}#widget-${widget.id}`;
        }
      } catch (error) {
        console.warn('[useWidgetAlerts] Error generating redirect_url:', error);
      }
    }

    // Otherwise, use default values from field definitions
    const defaults = alertFields.reduce(
      (acc, field) => {
        acc[field.name] = field.default;
        return acc;
      },
      {} as Record<string, any>,
    );

    // Add the generated redirect_url if we have one
    if (generatedRedirectUrl) {
      defaults.redirect_url = generatedRedirectUrl;
    }

    return defaults;
  }, [alertFields, dashboard, widget.id, activeSite]);

  // Derive hasAlerts from alertFields (no need for separate state)
  const hasAlerts = alertFields.length > 0;

  return {
    hasAlerts,
    alertFields,
    defaultValues,
    existingAlert,
    isLoadingAlert,
    isLoadingAlertFields,
  };
}
